<template>
  <div
    class="add-component-zone"
    :class="{
      'add-component-zone-hidden': !showAddZone,
      'add-component-zone-clickable': true
    }"
    @click="handleClick"
    @dragenter="handleDragEnter"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
    @dragover.prevent
  >
    <div class="add-component-content">
      <i class="pi pi-plus text-gray-400"></i>
      <span class="add-component-text">{{ message || 'Click to add component' }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { componentLogs } from '@/utils/logger'

interface Props {
  position: number
  parentId?: number
  message?: string
  compact?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  addComponent: [event: { position: number; parentId?: number }]
}>()

// Computed properties
const showAddZone = computed(() => {
  return true
})

// Event handlers
const handleDragEnter = (event: DragEvent) => {
  componentLogs.dropZone.dragEnter(props.position, props.parentId)
  event.preventDefault()
}

const handleDragLeave = (event: DragEvent) => {
  componentLogs.dropZone.dragLeave(props.position, props.parentId)
  event.preventDefault()
}

const handleDrop = (event: DragEvent) => {
  componentLogs.dropZone.drop(props.position, props.parentId)
  event.preventDefault()
  event.stopPropagation()

  // Emit add component event
  emit('addComponent', {
    position: props.position,
    parentId: props.parentId
  })
}

const handleClick = (event: MouseEvent) => {
  componentLogs.dropZone.clicked(props.position, props.parentId)

  event.stopPropagation()

  // Emit add component event
  emit('addComponent', {
    position: props.position,
    parentId: props.parentId
  })
}

// Maintain backward compatibility with existing click handler
const handleAddComponentClick = handleClick
</script>

<style scoped>
.add-component-zone {
  min-height: 60px;
  margin: 8px 0;
  border: 2px dashed transparent;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  opacity: 0.4;
  transform: scaleY(1);
}

.add-component-zone-hidden {
  display: none;
}

.add-component-zone:not(.add-component-zone-hidden):hover {
  opacity: 0.8;
}

.add-component-zone-clickable {
  cursor: pointer;
  border-color: #e5e7eb;
}

.add-component-zone-clickable:hover {
  border-color: #3b82f6;
  background-color: rgba(59, 130, 246, 0.05);
}

.add-component-zone-clickable:hover .add-component-content {
  color: #3b82f6;
}

.add-component-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: #6b7280;
  font-size: 14px;
}

.add-component-content i {
  font-size: 18px;
  margin-bottom: 4px;
}

.add-component-text {
  font-weight: 500;
}

/* Compact variant */
.add-component-zone.compact {
  min-height: 30px;
  margin: 4px 0;
}

.add-component-zone.compact .add-component-content {
  padding: 8px;
  font-size: 12px;
}

.add-component-zone.compact .add-component-content i {
  font-size: 14px;
  margin-bottom: 2px;
}
</style>